import { MenuItem, TextField } from '@mui/material'
import { FC } from 'react'
import { CurrencyField } from '@/components/common/CurrencyField'
import { CampoDiagnostico } from '@/types/estruturacao'

/**
 * O valor de UM campo do diagnóstico, no formato do upsert do backend: conforme o tipo,
 * um dos três é usado (TEXTO/OPCAO → valorTexto; NUMERO/MOEDA → valorNumero; DATA →
 * valorData). Os três nulos = resposta limpa.
 */
export interface ValorDiagnostico {
  valorTexto: string | null
  valorNumero: number | null
  valorData: string | null
}

export const valorVazio = (): ValorDiagnostico => ({
  valorTexto: null,
  valorNumero: null,
  valorData: null,
})

interface Props {
  campo: CampoDiagnostico
  valor: ValorDiagnostico
  onChange: (valor: ValorDiagnostico) => void
  disabled?: boolean
}

/**
 * Widget de um campo do Diagnóstico, escolhido pelo tipo cadastrado no produto
 * (campos são dado, não código): TEXTO → texto · NUMERO → numérico · MOEDA → R$ ·
 * DATA → data · OPCAO → select com as opções cadastradas.
 */
export const CampoDiagnosticoInput: FC<Props> = ({ campo, valor, onChange, disabled }) => {
  const comum = {
    label: campo.rotulo,
    required: !!campo.obrigatorio,
    disabled,
    fullWidth: true,
    size: 'small' as const,
  }

  switch (campo.tipo) {
    case 'NUMERO':
      return (
        <TextField
          {...comum}
          type="number"
          value={valor.valorNumero ?? ''}
          onChange={(e) =>
            onChange({
              ...valorVazio(),
              valorNumero: e.target.value === '' ? null : Number(e.target.value),
            })
          }
        />
      )
    case 'MOEDA':
      return (
        <CurrencyField
          {...comum}
          value={valor.valorNumero}
          onChange={(v) => onChange({ ...valorVazio(), valorNumero: v })}
        />
      )
    case 'DATA':
      return (
        <TextField
          {...comum}
          type="date"
          value={valor.valorData ?? ''}
          onChange={(e) =>
            onChange({ ...valorVazio(), valorData: e.target.value === '' ? null : e.target.value })
          }
          InputLabelProps={{ shrink: true }}
        />
      )
    case 'OPCAO':
      return (
        <TextField
          {...comum}
          select
          value={valor.valorTexto ?? ''}
          onChange={(e) =>
            onChange({ ...valorVazio(), valorTexto: e.target.value === '' ? null : e.target.value })
          }
        >
          <MenuItem value="">
            <em>Não informado</em>
          </MenuItem>
          {campo.opcoes.map((opcao) => (
            <MenuItem key={opcao} value={opcao}>
              {opcao}
            </MenuItem>
          ))}
        </TextField>
      )
    default:
      return (
        <TextField
          {...comum}
          value={valor.valorTexto ?? ''}
          onChange={(e) =>
            onChange({ ...valorVazio(), valorTexto: e.target.value === '' ? null : e.target.value })
          }
        />
      )
  }
}

export default CampoDiagnosticoInput
